/**
 * Custom icons that have no @tabler/icons-react equivalent, built from the exact
 * vector geometry of their Figma iconography nodes (polaris-design-system, node 12-339).
 * Typed to match Tabler's IconProps so they drop into ICON_REGISTRY unchanged.
 */
import { forwardRef } from "react";

type UikitIconProps = Omit<React.ComponentPropsWithoutRef<"svg">, "stroke"> & {
	size?: string | number;
	stroke?: string | number;
	title?: string;
};

/**
 * Desktop + webcam combo ("Camera and screen"). Figma node 2082:3082 — a single
 * flattened outline path, so it honours `color`/`stroke` like any Tabler outline icon.
 */
export const DeviceDesktopAndCamera = forwardRef<SVGSVGElement, UikitIconProps>(
	function DeviceDesktopAndCamera({ size = 24, color = "currentColor", stroke = 2, ...rest }, ref) {
		return (
			<svg
				ref={ref}
				xmlns="http://www.w3.org/2000/svg"
				width={size}
				height={size}
				viewBox="0 0 24 24"
				fill="none"
				stroke={color}
				strokeWidth={stroke}
				strokeLinecap="round"
				strokeLinejoin="round"
				{...rest}
			>
				<path d="M7 16v5M8 4H3a1 1 0 0 0 -1 1v10a1 1 0 0 0 1 1h5M4.5 21h15.734a1 1 0 0 0 .857 -1.514L19 16M8 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0M12 10a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
			</svg>
		);
	}
);

/**
 * Text-selection cursor ("I-beam"). Figma node 1820:22274 — a single filled path,
 * kept monochrome (fill honours `color`) so it fits the icon system. No Tabler equivalent.
 */
export const MousePointerText = forwardRef<SVGSVGElement, UikitIconProps>(function MousePointerText(
	{ size = 24, color = "currentColor", stroke: _stroke, ...rest },
	ref
) {
	return (
		<svg
			ref={ref}
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill={color}
			{...rest}
		>
			<path d="M11 17V13H10C9.44772 13 9 12.5523 9 12C9 11.4477 9.44772 11 10 11H11V7C11 6.46957 10.7891 5.96101 10.4141 5.58594C10.039 5.21087 9.53043 5 9 5C8.44772 5 8 4.55228 8 4C8 3.44772 8.44772 3 9 3C10.0609 3 11.078 3.42173 11.8281 4.17188C11.8876 4.23135 11.9447 4.29274 12 4.35547C12.0553 4.29274 12.1124 4.23135 12.1719 4.17188C12.922 3.42173 13.9391 3 15 3C15.5523 3 16 3.44772 16 4C16 4.55228 15.5523 5 15 5C14.4696 5 13.961 5.21087 13.5859 5.58594C13.2109 5.96101 13 6.46957 13 7V11H14C14.5523 11 15 11.4477 15 12C15 12.5523 14.5523 13 14 13H13V17C13 17.5304 13.2109 18.039 13.5859 18.4141C13.961 18.7891 14.4696 19 15 19C15.5523 19 16 19.4477 16 20C16 20.5523 15.5523 21 15 21C13.9391 21 12.922 20.5783 12.1719 19.8281C12.1122 19.7685 12.0554 19.7065 12 19.6436C11.9446 19.7065 11.8878 19.7685 11.8281 19.8281C11.078 20.5783 10.0609 21 9 21C8.44772 21 8 20.5523 8 20C8 19.4477 8.44772 19 9 19C9.53043 19 10.039 18.7891 10.4141 18.4141C10.7891 18.039 11 17.5304 11 17Z" />
		</svg>
	);
});

// Angular (conic) rainbow from Figma node 46:3825 — a fixed multicolor asset, so it
// intentionally ignores `color`/`stroke`. Rendered as a clipped span (SVG has no
// portable conic-gradient fill). Ring stroke #E9EDF1 matches the Figma border.
export const COLOR_WHEEL_GRADIENT =
	"conic-gradient(from 90deg, #FF0800 0deg, #FFFC00 63deg, #00FF08 126deg, #00FAFF 180deg, #0005FF 241.2deg, #FF00F6 302.4deg, #FF0800 360deg)";

export const ColorWheel = forwardRef<HTMLSpanElement, UikitIconProps>(function ColorWheel(
	{ size = 24, className, style, title, "aria-label": ariaLabel, "aria-hidden": ariaHidden },
	ref
) {
	return (
		<span
			ref={ref}
			className={className}
			role="img"
			aria-label={ariaHidden ? undefined : ariaLabel}
			aria-hidden={ariaHidden}
			title={title}
			style={{
				display: "inline-block",
				width: size,
				height: size,
				borderRadius: "50%",
				background: COLOR_WHEEL_GRADIENT,
				boxShadow: "inset 0 0 0 1px #E9EDF1",
				...style,
			}}
		/>
	);
});
